import Loading from '@/components/Loading/Loading';
import { ReleaseInfo } from '@/generated/ReleaseInfo';
import { _noop, _notNil } from '@/littledash';
import InVivoError from '@/model/InVivoError.ts';
import { ExceptionHandler } from '@/utils/ExceptionHandler';
import { isChunkLoadError, reloadForRelease } from '@/utils/ReleaseUpdate.utils';
import React, { type ReactNode } from 'react';
import ErrorPage from './ErrorPage';

interface Props {
  children: ReactNode;
}

interface State {
  error: boolean;
  chunkLoadError: boolean;
  recovering: boolean;
}

const chunkReloadCommitKey = 'chunk-reload.commit';

type ChunkReloadDecision = 'reload' | 'offline' | 'already-attempted';

/**
 * A stale bundle asking for a chunk that is no longer deployed can only be recovered by
 * booting the current release, so one reload is allowed per tab per build. The attempt is
 * recorded against the commit id of the build that failed: if we come back up on that same
 * build the reload did not help and we stop, but once a new build is serving the ids differ
 * and a future chunk error is recoverable again. Offline is excluded because a reload cannot
 * fix it and would replace our error page with the browser's own. The refusals are told apart
 * rather than collapsed into a boolean so each can be reported for what it is.
 */
const chunkReloadDecision = (): ChunkReloadDecision => {
  if (!navigator.onLine) {
    return 'offline';
  }
  const attemptedCommitId = sessionStorage.getItem(chunkReloadCommitKey);
  if (attemptedCommitId === ReleaseInfo.commitId) {
    return 'already-attempted';
  }
  if (_notNil(attemptedCommitId)) {
    sessionStorage.removeItem(chunkReloadCommitKey);
  }
  return 'reload';
};

class ErrorBoundary extends React.Component<Props, State> {
  state: State = {
    error: false,
    chunkLoadError: false,
    recovering: false,
  };

  static getDerivedStateFromError(error: Error): State {
    const chunkLoadError = isChunkLoadError(error);
    return {
      error: true,
      chunkLoadError,
      recovering: chunkLoadError,
    };
  }

  componentDidCatch(error: Error): void {
    if (this.state.chunkLoadError) {
      const decision = chunkReloadDecision();
      if (decision === 'reload') {
        sessionStorage.setItem(chunkReloadCommitKey, ReleaseInfo.commitId);
        ExceptionHandler.captureException(
          new InVivoError(`Chunk load failed, reloading: ${error?.message ?? '???'}`, {
            cause: error,
            slug: 'chunk-load-recovered',
            level: 'warning',
          })
        );
        reloadForRelease().catch(_noop);
        return;
      }
      // Kept apart from the unrecoverable case because Sentry groups on the slug and cannot
      // segment on the online flag: an offline failure is an unactionable user-environment
      // condition, while a failure after a reload onto the same build is a deploy defect.
      ExceptionHandler.captureException(
        decision === 'offline'
          ? new InVivoError(`Chunk load failed while offline: ${error?.message ?? '???'}`, {
              cause: error,
              slug: 'chunk-load-offline',
              level: 'warning',
            })
          : new InVivoError(`Chunk load failed after reload: ${error?.message ?? '???'}`, {
              cause: error,
              slug: 'chunk-load-unrecoverable',
              level: 'error',
            })
      );
      this.setState({ recovering: false });
      return;
    }
    ExceptionHandler.captureException(
      new InVivoError(`Unhandled exception: ${error?.message ?? '???'}`, {
        cause: error,
        slug: 'error-boundary',
        level: 'fatal',
      })
    );
  }

  resetError = (): void => this.setState({ error: false, chunkLoadError: false, recovering: false });

  render(): ReactNode {
    if (this.state.error) {
      return this.state.recovering ? <Loading /> : <ErrorPage resetError={this.resetError} />;
    }
    return <>{this.props.children}</>;
  }
}

export default ErrorBoundary;
